import { fmtDate } from "../format.ts";
import type { PositionSnapshot } from "../metrics.ts";

// ---- chips tổng quan (#pos-stats) ----

export type PositionStatsProps = {
  snaps: readonly PositionSnapshot[];
  /** interviewWeeklyTarget của CONFIG — hiện trong nhãn "(<N/wk)". */
  target: number;
};

export function PositionStats({ snaps, target }: PositionStatsProps) {
  const count = (s: PositionSnapshot["status"]) => snaps.filter((x) => x.status === s).length;

  return (
    <>
      <span className="pos-chip"><b>{snaps.length}</b> positions tracked</span>
      <span className="pos-chip red"><b>{count("red")}</b> behind target (&lt;{target}/wk)</span>
      <span className="pos-chip green"><b>{count("ontrack")}</b> on track</span>
      <span className="pos-chip teal"><b>{count("filled")}</b> filled</span>
    </>
  );
}

// ---- lưới card từng vị trí (#pos-grid) ----

function badgeText(s: PositionSnapshot): string {
  if (s.status === "filled") return `FILLED · ${s.hires} HIRE${s.hires > 1 ? "S" : ""}`;
  if (s.status === "ontrack") return `ON TRACK · ${s.interviews}/${s.target}`;
  return `BEHIND · ${s.interviews}/${s.target} (−${s.gap})`;
}

type BarProps = {
  label: string;
  value: number;
  /** Mốc 100% của bar = số contacted của chính vị trí đó. */
  max: number;
  /** Hàng Interviews được tô màu theo status (đây là chỉ số bị đo target). */
  highlight?: boolean;
  status: PositionSnapshot["status"];
};

function Bar({ label, value, max, highlight, status }: BarProps) {
  const fillClass = highlight ? (status === "red" ? "warn" : "ok") : "";
  return (
    <div className={highlight ? "pos-row hl" : "pos-row"}>
      <span className="lbl">{label}</span>
      <span className="pos-bar">
        <i className={fillClass} style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
      </span>
      <span className="num">{value}</span>
    </div>
  );
}

export type PositionsProps = { snaps: readonly PositionSnapshot[] };

export function Positions({ snaps }: PositionsProps) {
  if (!snaps.length) return <div className="kpi-empty">No position data yet.</div>;

  return (
    <>
      {snaps.map((s) => {
        const max = Math.max(s.contacted, 1);
        const bar = (label: string, value: number, highlight?: boolean) => (
          <Bar key={label} label={label} value={value} max={max} highlight={highlight} status={s.status} />
        );
        return (
          <div className={`pos-card ${s.status}`} key={s.position}>
            <div className="pos-head">
              <h3>{s.position}</h3>
              <span className={`pos-badge ${s.status}`}>{badgeText(s)}</span>
            </div>
            <div className="pos-week">
              {s.weeks} wk logged · latest {fmtDate(s.weekEnding)}
              {/* {" "} giữ khoảng trắng mà bản innerHTML có sẵn qua newline */}
              {s.stale ? <>{" "}<span className="stale"> · ⚠ NOT UPDATED THIS WEEK</span></> : null}
            </div>
            {bar("Contacted", s.contacted)}
            {bar("Responses", s.responses)}
            {bar("Applications", s.applications)}
            {bar("Interviews", s.interviews, true)}
            {bar("Offers", s.offers)}
            {bar("Hires", s.hires)}
            <div className="pos-rates">
              resp {s.rates.responseRate}% · apply {s.rates.applicationRate}% · interview{" "}
              {s.rates.interviewRate}% · offer {s.rates.offerRate}%
            </div>
            {s.notes ? <div className="pos-note">▸ {s.notes}</div> : null}
          </div>
        );
      })}
    </>
  );
}
